import { useMemo } from "react";
import { authorize } from "@playbookai/auth-client/rbac";
import {
  SOLVE_PRIVILEGES,
  type Privilege,
  type SolveDetail,
  type SolvePhase,
  type SolveRole,
} from "@playbookai/types";

/** Solve-management actions the card/workspace kebab gates on. Lock-independent
 *  (a lock gates `edit`, not settings/delete), so a LIST row — which carries
 *  `myRole`/`isFacilitator` but no phase-lock state — can compute them too. */
export interface SolveActionPermissions {
  /** May open the settings/edit form (Owner + Facilitator; `change_settings`). */
  readonly canChangeSettings: boolean;
  /** May delete the solve (Owner only; excluded from the Facilitator overlay). */
  readonly canDelete: boolean;
  /** May add/remove members and change member roles (Owner + Facilitator). */
  readonly canManageMembers: boolean;
  /** May grant the Facilitator overlay (Owner only). */
  readonly canGrantFacilitator: boolean;
  /** May revoke the Facilitator overlay (Owner only). */
  readonly canRevokeFacilitator: boolean;
}

/**
 * The lock-independent management perms, derived from the shared `authorize()`
 * over just the caller's role + facilitator overlay — so a dashboard card/row
 * and the workspace agree, and neither offers an action the server would 403.
 */
export function computeSolveActionPermissions(
  snapshot: { myRole?: SolveRole | null; isFacilitator?: boolean } | undefined,
): SolveActionPermissions {
  const role = snapshot?.myRole ?? null;
  const isFacilitator = snapshot?.isFacilitator ?? false;
  const can = (action: Privilege): boolean =>
    authorize({ privileges: SOLVE_PRIVILEGES, role, isFacilitator, action })
      .allowed;
  return {
    canChangeSettings: can("change_settings"),
    canDelete: can("delete"),
    canManageMembers: can("manage_members"),
    canGrantFacilitator: can("grant_facilitator"),
    canRevokeFacilitator: can("revoke_facilitator"),
  };
}

/**
 * The SOLVE lock/role affordance decisions, ALL derived from the shared pure
 * `authorize()` (ADR-0019) over the capability snapshot the detail read returns
 * — so what the UI offers can never drift from what the server enforces.
 */
export interface SolvePermissions {
  /** May advance the durable SOLVE phase (Owner/Facilitator). */
  readonly canAdvancePhase: boolean;
  /** May complete (close) the SOLVE — `close_instance`; Owner + Facilitator. */
  readonly canComplete: boolean;
  /** May toggle the canvas lock (Owner/Facilitator). */
  readonly canManageCanvasLock: boolean;
  /** May lock/unlock individual phases (Owner/Facilitator). */
  readonly canManagePhaseLock: boolean;
  /** Edits are never lock-gated for this caller (Owner/Facilitator). */
  readonly isLockExempt: boolean;
  /** Holds `edit` at all — distinguishes a Collaborator from a read-only Viewer. */
  readonly canEditAtAll: boolean;
  /** May edit content in `phase` given the current canvas + phase lock state. */
  readonly canEditPhase: (phase: SolvePhase) => boolean;
  /** May open the solve settings/edit form (Owner + Facilitator; `change_settings`). */
  readonly canChangeSettings: boolean;
  /** May delete the solve (Owner only; excluded from the Facilitator overlay). */
  readonly canDelete: boolean;
  /** May add/remove members and change member roles (Owner + Facilitator). */
  readonly canManageMembers: boolean;
  /** May grant the Facilitator overlay (Owner only). */
  readonly canGrantFacilitator: boolean;
  /** May revoke the Facilitator overlay (Owner only). */
  readonly canRevokeFacilitator: boolean;
}

/**
 * Pure computation (no React) so it unit-tests as a persona × lock matrix.
 * `LOCK_EXEMPT_ROLES` in rbac is private by design — `isLockExempt` is derived
 * by asking `authorize()` an `edit`-under-lock question, not by importing the set.
 */
export function computeSolvePermissions(
  snapshot: SolveDetail | undefined,
): SolvePermissions {
  const role = snapshot?.myRole ?? null;
  const isFacilitator = snapshot?.isFacilitator ?? false;
  // Fail-closed until the snapshot loads: assume locked.
  const canvasLocked = snapshot?.canvasLocked ?? true;
  const lockedPhases = snapshot?.lockedPhases ?? [];

  const can = (
    action: Privilege,
    lock?: { canvasLocked: boolean; phaseLocked: boolean },
  ): boolean =>
    authorize({
      privileges: SOLVE_PRIVILEGES,
      role,
      isFacilitator,
      action,
      // Omit `lock` entirely when absent (exactOptionalPropertyTypes) —
      // authorize() only lock-gates `edit` when a lock context is present.
      ...(lock ? { lock } : {}),
    }).allowed;

  return {
    canAdvancePhase: can("advance_phase"),
    // PB-5272: the workspace's "Complete Solve" action. The same privilege the
    // `POST :solveId/close` route's guard requires, asked through the same
    // authorize() — so a Collaborator or Viewer is never offered a control the
    // server would 403. Whether the SOLVE is READY to complete is a separate,
    // content-based question the server answers on its own endpoint.
    canComplete: can("close_instance"),
    // Require BOTH directions — the toggle both locks and unlocks, so a role
    // holding only one (a future asymmetric role) must not get the control.
    canManageCanvasLock: can("lock_canvas") && can("unlock_canvas"),
    canManagePhaseLock: can("lock_phase") && can("unlock_phase"),
    isLockExempt: can("edit", { canvasLocked: true, phaseLocked: false }),
    canEditAtAll: can("edit"),
    canEditPhase: (phase: SolvePhase): boolean =>
      can("edit", {
        canvasLocked,
        phaseLocked: lockedPhases.includes(phase),
      }),
    // Solve-management actions (shared with the dashboard rows) — drives the
    // kebab so a Viewer/Collaborator is never shown Edit/Delete the server 403s.
    ...computeSolveActionPermissions(snapshot),
  };
}

/**
 * Select the secure socket admission mode from the same coarse edit decision
 * enforced by Collaboration: Viewer is observe-only; a Collaborator may request
 * edit only while the canvas is unlocked; Owner and Facilitator are lock-exempt.
 * Phase locks remain per-widget gates and do not make the whole socket observe.
 */
export function computeSolveSecureConnectionMode(
  snapshot: SolveDetail | undefined,
): "edit" | "participate" {
  const permissions = computeSolvePermissions(snapshot);
  return permissions.isLockExempt ||
    (permissions.canEditAtAll && !(snapshot?.canvasLocked ?? true))
    ? "edit"
    : // "participate", NOT "observe". An observe connection is deliberately
      // presence-less — the lifecycle forces `localClientId = null` and the
      // adapter builds no awareness — so a SOLVE Viewer was invisible to
      // everyone else on the canvas. Nobody could tell whether a reviewer was
      // watching a walkthrough.
      //
      // `participate` is read-only for the DOCUMENT and still publishes
      // awareness, which is the behaviour the platform already documents:
      // "awareness flows both ways on read-only connections — presence survives
      // enforcement". It is also VSM's normal path for a viewer on the canvas,
      // covered by collab's own integration test ("a Viewer's socket is
      // read-only — doc writes NACKed, awareness still flows").
      //
      // This does NOT grant write access: the server gates mutations on the
      // authorization snapshot, not on the connection mode, and `participate`
      // maps to an observe-scoped ticket. SOLVE has no monitor-only surface, so
      // `observe` had no remaining caller here.
      "participate";
}

/** Memoized hook wrapper for component consumers. */
export function useSolvePermissions(
  snapshot: SolveDetail | undefined,
): SolvePermissions {
  return useMemo(() => computeSolvePermissions(snapshot), [snapshot]);
}
